/**
 * Collection Template JavaScript
 * Custom elements for sort dropdown and filter drawer
 */

/**
 * Custom Sort Dropdown
 * Replaces native select with custom accessible dropdown
 */
class CollectionSortDropdown extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('[data-sort-toggle]');
    this.options = this.querySelector('[data-sort-options]');
    this.hiddenInput = this.querySelector('[data-sort-input]');
    this.optionButtons = this.querySelectorAll('[data-sort-value]');

    this.bindEvents();
  }

  bindEvents() {
    if (this.toggle) {
      this.toggle.addEventListener('click', this.onToggle.bind(this));
    }

    this.optionButtons.forEach(option => {
      option.addEventListener('click', this.onSelect.bind(this));
    });

    document.addEventListener('click', this.onClickOutside.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  onToggle(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    const activeOption = this.querySelector('[aria-selected="true"]');
    if (activeOption) {
      activeOption.focus();
    }
  }

  close() {
    this.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
  }

  onSelect(event) {
    event.stopPropagation();
    const value = event.target.dataset.sortValue;

    this.optionButtons.forEach(btn => {
      btn.classList.remove('collection-sort-dropdown__option--active');
      btn.setAttribute('aria-selected', 'false');
    });

    event.target.classList.add('collection-sort-dropdown__option--active');
    event.target.setAttribute('aria-selected', 'true');

    this.hiddenInput.value = value;

    this.close();

    // Dispatch after close to ensure iOS handles it properly
    setTimeout(() => {
      this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    }, 10);
  }

  onClickOutside(event) {
    if (!this.contains(event.target) && this.isOpen) {
      this.close();
    }
  }

  onKeyDown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        this.close();
        this.toggle.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextOption();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPrevOption();
        break;
      case 'Enter':
      case ' ':
        if (document.activeElement.dataset.sortValue) {
          event.preventDefault();
          document.activeElement.click();
        }
        break;
    }
  }

  focusNextOption() {
    const focused = document.activeElement;
    const options = Array.from(this.optionButtons);
    const currentIndex = options.indexOf(focused);
    const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
    options[nextIndex].focus();
  }

  focusPrevOption() {
    const focused = document.activeElement;
    const options = Array.from(this.optionButtons);
    const currentIndex = options.indexOf(focused);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
    options[prevIndex].focus();
  }
}

customElements.define('collection-sort-dropdown', CollectionSortDropdown);

/**
 * Collection Filter Drawer
 * Mobile filter drawer that slides from right
 */
class CollectionFilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-drawer-close]');
    this.closeButtons = this.querySelectorAll('[data-drawer-close]');
    this.applyButton = this.querySelector('[data-drawer-apply]');

    this.bindEvents();
    this.setupTriggers();
  }

  bindEvents() {
    this.closeButtons.forEach(btn => {
      btn.removeEventListener('click', this.boundClose);
      this.boundClose = this.close.bind(this);
      btn.addEventListener('click', this.boundClose);
    });

    if (this.applyButton) {
      this.applyButton.removeEventListener('click', this.boundApply);
      this.boundApply = this.onApply.bind(this);
      this.applyButton.addEventListener('click', this.boundApply);
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setupTriggers() {
    const triggers = document.querySelectorAll('[data-drawer-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', this.open.bind(this));
    });
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;

    const firstInput = this.querySelector('input:not([type="hidden"])');
    if (firstInput) {
      firstInput.focus();
    }
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.isOpen = false;

    const trigger = document.querySelector('[data-drawer-trigger]');
    if (trigger) {
      trigger.focus();
    }
  }

  onApply() {
    const form = this.querySelector('form');
    if (form) {
      const event = new Event('input', { bubbles: true });
      const firstInput = form.querySelector('input');
      if (firstInput) {
        firstInput.dispatchEvent(event);
      }
    }

    this.close();
  }
}

customElements.define('collection-filter-drawer', CollectionFilterDrawer);

/**
 * Fallback for facets.js menu-drawer compatibility
 * facets.js expects a menu-drawer element with bindEvents method
 * This provides a no-op implementation if menu-drawer isn't already defined
 */
if (!customElements.get('menu-drawer')) {
  class MenuDrawerFallback extends HTMLElement {
    bindEvents() {
      // No-op for compatibility with facets.js
    }
  }
  customElements.define('menu-drawer', MenuDrawerFallback);
}

/**
 * Add to Cart functionality for collection product cards
 * Uses event delegation to work with AJAX-loaded content
 */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('.collection-product-card__button[data-variant-id]');
  if (!button) return;

  event.preventDefault();

  const variantId = button.dataset.variantId;

  if (button.disabled || !variantId) return;

  button.disabled = true;
  const originalText = button.innerHTML;
  button.innerHTML = '<span>Ajout en cours...</span>';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (response.ok) {
      button.innerHTML = '<span>Ajouté !</span>';

      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      cartCountElements.forEach(el => {
        el.textContent = cartData.item_count;
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));

      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    } else {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add to cart');
    }
  } catch (error) {
    console.error('Add to cart error:', error);
    button.innerHTML = '<span>Erreur</span>';

    setTimeout(() => {
      button.innerHTML = originalText;
      button.disabled = false;
    }, 2000);
  }
});
